import React, { useState } from 'react';
import { api, PerformanceBenchmarkReport } from '../api';
import type { HealthLevel } from '../labels';
import { Callout, Input, Section, Table } from './primitives';
import { StatusBadge } from './ui';

/** Total response time: under 400ms is healthy, under a second needs a look, slower needs action. */
function latencyLevel(totalMs: number): HealthLevel {
  if (totalMs < 400) return 'ok';
  if (totalMs < 1000) return 'warning';
  return 'critical';
}

interface PerformanceBenchmarkCardProps {
  environmentId: string;
}

export const PerformanceBenchmarkCard: React.FC<PerformanceBenchmarkCardProps> = ({ environmentId }) => {
  const [report, setReport] = useState<PerformanceBenchmarkReport | null>(null);
  const [storefrontUrl, setStorefrontUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runBenchmark = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.runPerfBenchmark(environmentId, storefrontUrl, ['/', '/graphql', '/customer/account/login']);
      setReport(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The measurement failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section
      title="Storefront response time"
      description={report ? `Average ${report.average_latency_ms} ms` : undefined}
      testId="performance-benchmark"
      actions={
        <form className="ssh-run-form" onSubmit={runBenchmark}>
          <Input
            type="text"
            placeholder="https://store.example.com (optional)"
            value={storefrontUrl}
            onChange={(e) => setStorefrontUrl(e.target.value)}
            aria-label="Storefront URL"
          />
          <button type="submit" disabled={loading} className="button button--secondary">
            {loading ? 'Measuring' : report ? 'Measure again' : 'Measure response time'}
          </button>
        </form>
      }
    >
      {error ? (
        <Callout tone="danger" title="The measurement did not finish">
          {error}
        </Callout>
      ) : null}

      {report ? (
        <Table label="Response times" compact>
          <thead>
            <tr>
              <th scope="col">Route</th>
              <th scope="col">HTTP</th>
              <th scope="col">First byte</th>
              <th scope="col">Size</th>
              <th scope="col">Total</th>
            </tr>
          </thead>
          <tbody>
            {report.samples.map((s, i) => (
              <tr key={`${s.path}-${i}`}>
                <th scope="row" className="font-mono">
                  {s.path}
                </th>
                <td className="tabular-nums">{s.http_status}</td>
                <td className="tabular-nums">{s.ttfb_ms} ms</td>
                <td className="tabular-nums">{(s.size_content / 1024).toFixed(1)} KB</td>
                <td>
                  <StatusBadge kind="level" value={latencyLevel(s.total_ms)} label={`${s.total_ms} ms`} className="tabular-nums" />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="quiet-empty">Not measured yet</p>
      )}
    </Section>
  );
};
